import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MOCK_AVAILABLE_JOBS } from '../data/mockData';
import { JobStatus } from '../types/job';

const TOKEN_STORAGE_KEY = '@cp_token';
const CERTS_STORAGE_KEY = '@commonplace_certifications';
const ACTIVE_JOBS_STORAGE_KEY = '@commonplace_active_jobs';
const EARNINGS_STORAGE_KEY = '@commonplace_earnings';
const JOB_HISTORY_STORAGE_KEY = '@commonplace_job_history';
const DRIVER_STORAGE_KEY = '@commonplace_driver';
const QUIZ_FAILURES_STORAGE_KEY = '@commonplace_quiz_failures';
const ACCOUNT_HOLD_STORAGE_KEY = '@commonplace_account_hold';
const DATA_VERSION_KEY = '@commonplace_data_version';
// Bump this to clear all persisted data on next app load
const CURRENT_DATA_VERSION = '15';

// ─── Auth Slice ─────────────────────────────────────────
/** Per-course consecutive failure counts — keyed by courseId */
interface QuizFailures {
  [courseId: string]: number;
}

interface AuthState {
  token: string | null;
  driver: {
    id?: string;
    name: string;
    email?: string;
    phone?: string;
    accountNumber?: string;
    baseLat?: number;
    baseLng?: number;
    baseAddress?: string;
    vehicleType?: string;
    vehicleDetails?: string;
    insuranceProvider?: string;
    workingRadiusMiles?: number;
    workingRadius?: number;
    isFleetManager?: boolean;
    fleetManagerId?: string;
  } | null;
  certifications: string[]; // array of approved categorySlug strings
  quizFailures: QuizFailures; // consecutive fail count per course
  accountOnHold: boolean; // true = locked until admin approval
}

const authSlice = createSlice({
  name: 'auth',
  initialState: { token: null, driver: null, certifications: [], quizFailures: {}, accountOnHold: false } as AuthState,
  reducers: {
    setToken(state, action: PayloadAction<string | null>) {
      state.token = action.payload;
      if (action.payload) {
        AsyncStorage.setItem(TOKEN_STORAGE_KEY, action.payload).catch(() => {});
      } else {
        AsyncStorage.removeItem(TOKEN_STORAGE_KEY).catch(() => {});
      }
    },
    setDriver(state, action: PayloadAction<AuthState['driver']>) {
      state.driver = action.payload;
      if (action.payload) {
        AsyncStorage.setItem(DRIVER_STORAGE_KEY, JSON.stringify(action.payload)).catch(() => {});
        // The SERVER status is authoritative for the account hold. Whenever a fresh driver
        // profile arrives (login/OTP/boot /drivers/me), reconcile the sticky local hold so a
        // driver whose status is no longer ON_HOLD (admin lifted it, or it was never set) is
        // never stuck on the "Account On Hold" screen. THE fix for "on hold but it's not".
        const status = (action.payload as any).status;
        if (status) {
          const held = status === 'ON_HOLD';
          state.accountOnHold = held;
          if (held) {
            AsyncStorage.setItem(ACCOUNT_HOLD_STORAGE_KEY, 'true').catch(() => {});
          } else {
            AsyncStorage.removeItem(ACCOUNT_HOLD_STORAGE_KEY).catch(() => {});
            state.quizFailures = {};
            AsyncStorage.removeItem(QUIZ_FAILURES_STORAGE_KEY).catch(() => {});
          }
        }
      }
    },
    setCertifications(state, action: PayloadAction<string[]>) {
      state.certifications = action.payload;
      AsyncStorage.setItem(CERTS_STORAGE_KEY, JSON.stringify(action.payload)).catch(() => {});
    },
    setQuizFailures(state, action: PayloadAction<QuizFailures>) {
      state.quizFailures = action.payload;
    },
    recordQuizFailure(state, action: PayloadAction<string>) {
      const courseId = action.payload;
      const current = (state.quizFailures[courseId] || 0) + 1;
      state.quizFailures[courseId] = current;
      AsyncStorage.setItem(QUIZ_FAILURES_STORAGE_KEY, JSON.stringify(state.quizFailures)).catch(() => {});
      if (current >= 3) {
        state.accountOnHold = true;
        AsyncStorage.setItem(ACCOUNT_HOLD_STORAGE_KEY, 'true').catch(() => {});
        // Report hold to backend so admin can see it
        import('../services/api').then(({ api }) => {
          api.post('/training/report-hold', { reason: `Quiz failed ${current}x for ${courseId}` }).catch(() => {});
        }).catch(() => {});
      }
    },
    clearQuizFailures(state, action: PayloadAction<string>) {
      const courseId = action.payload;
      delete state.quizFailures[courseId];
      AsyncStorage.setItem(QUIZ_FAILURES_STORAGE_KEY, JSON.stringify(state.quizFailures)).catch(() => {});
    },
    setAccountOnHold(state, action: PayloadAction<boolean>) {
      state.accountOnHold = action.payload;
      if (action.payload) {
        AsyncStorage.setItem(ACCOUNT_HOLD_STORAGE_KEY, 'true').catch(() => {});
      } else {
        AsyncStorage.removeItem(ACCOUNT_HOLD_STORAGE_KEY).catch(() => {});
      }
    },
    logout(state) {
      state.token = null;
      state.driver = null;
      state.certifications = [];
      state.quizFailures = {};
      state.accountOnHold = false;
      AsyncStorage.removeItem(TOKEN_STORAGE_KEY).catch(() => {});
      AsyncStorage.removeItem(DRIVER_STORAGE_KEY).catch(() => {});
      AsyncStorage.removeItem(CERTS_STORAGE_KEY).catch(() => {});
      AsyncStorage.removeItem(QUIZ_FAILURES_STORAGE_KEY).catch(() => {});
      AsyncStorage.removeItem(ACCOUNT_HOLD_STORAGE_KEY).catch(() => {});
    },
  },
});

// ─── Jobs Slice ─────────────────────────────────────────
interface EarningsData {
  todayEarnings: number;
  weekEarnings: number;
  pendingBalance: number;
  totalEarnings: number;
  completedCount: number;
}

interface JobsState {
  available: any[];
  activeJobs: any[];      // multiple simultaneous active deliveries
  online: boolean;
  completedJobs: any[];
  earnings: EarningsData;
}

function persistActiveJobs(jobs: any[]) {
  AsyncStorage.setItem(ACTIVE_JOBS_STORAGE_KEY, JSON.stringify(jobs)).catch(() => {});
}

const jobsSlice = createSlice({
  name: 'jobs',
  initialState: {
    available: MOCK_AVAILABLE_JOBS,
    activeJobs: [],
    online: false,
    completedJobs: [],
    earnings: {
      todayEarnings: 0,
      weekEarnings: 0,
      pendingBalance: 0,
      totalEarnings: 0,
      completedCount: 0,
    },
  } as JobsState,
  reducers: {
    setAvailableJobs(state, action: PayloadAction<any[]>) {
      state.available = action.payload;
    },
    /** Add a job to active deliveries */
    addActiveJob(state, action: PayloadAction<any>) {
      const job = action.payload;
      // Don't add duplicates
      if (!state.activeJobs.find(j => j.id === job.id)) {
        state.activeJobs.push(job);
      } else {
        // Update existing
        state.activeJobs = state.activeJobs.map(j => j.id === job.id ? job : j);
      }
      persistActiveJobs(state.activeJobs);
    },
    /** Update status for a specific active job */
    advanceJobStatus(state, action: PayloadAction<{ jobId: string; status: JobStatus }>) {
      const { jobId, status } = action.payload;
      state.activeJobs = state.activeJobs.map(j =>
        j.id === jobId ? { ...j, status } : j
      );
      persistActiveJobs(state.activeJobs);
    },
    /** Remove an active job (e.g. when completed) */
    removeActiveJob(state, action: PayloadAction<string>) {
      state.activeJobs = state.activeJobs.filter(j => j.id !== action.payload);
      persistActiveJobs(state.activeJobs);
    },
    /** Set all active jobs at once (for hydration) */
    setActiveJobs(state, action: PayloadAction<any[]>) {
      state.activeJobs = action.payload;
    },
    removeJob(state, action: PayloadAction<string>) {
      state.available = state.available.filter(j => j.id !== action.payload);
    },
    setOnline(state, action: PayloadAction<boolean>) {
      state.online = action.payload;
    },
    setEarnings(state, action: PayloadAction<EarningsData>) {
      state.earnings = action.payload;
    },
    setCompletedJobs(state, action: PayloadAction<any[]>) {
      state.completedJobs = action.payload;
    },
    completeJob(state, action: PayloadAction<any>) {
      const job = action.payload;
      // Deduplicate — don't add if job already in completed list
      if (!state.completedJobs.some(j => j.id === job.id)) {
        state.completedJobs.push(job);
      }
      state.activeJobs = state.activeJobs.filter(j => j.id !== job.id);
      persistActiveJobs(state.activeJobs);
      AsyncStorage.setItem(JOB_HISTORY_STORAGE_KEY, JSON.stringify(state.completedJobs)).catch(() => {});
    },
    addEarnings(state, action: PayloadAction<number>) {
      const payout = action.payload;
      state.earnings.todayEarnings += payout;
      state.earnings.weekEarnings += payout;
      state.earnings.pendingBalance += payout;
      state.earnings.totalEarnings += payout;
      state.earnings.completedCount += 1;
      AsyncStorage.setItem(EARNINGS_STORAGE_KEY, JSON.stringify(state.earnings)).catch(() => {});
    },
  },
  // On logout, wipe this driver's jobs/history/earnings (state + storage) so the NEXT
  // driver to log in on a shared device never inherits the previous driver's active
  // jobs, completed history, or earnings totals.
  extraReducers: (builder) => {
    builder.addCase(authSlice.actions.logout, (state) => {
      state.activeJobs = [];
      state.completedJobs = [];
      state.earnings = { todayEarnings: 0, weekEarnings: 0, pendingBalance: 0, totalEarnings: 0, completedCount: 0 };
      AsyncStorage.multiRemove([
        ACTIVE_JOBS_STORAGE_KEY, JOB_HISTORY_STORAGE_KEY, EARNINGS_STORAGE_KEY, '@commonplace_active_job',
      ]).catch(() => {});
    });
  },
});

export const { setToken, setDriver, setCertifications, setQuizFailures, recordQuizFailure, clearQuizFailures, setAccountOnHold, logout } = authSlice.actions;
export const { setAvailableJobs, addActiveJob, advanceJobStatus, removeActiveJob, removeJob, setOnline, completeJob, addEarnings, setActiveJobs } = jobsSlice.actions;

// Backward compat alias
export const setActiveJob = addActiveJob;

export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    jobs: jobsSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/** Load persisted state from AsyncStorage into Redux */
export async function loadPersistedState() {
  try {
    const storedVersion = await AsyncStorage.getItem(DATA_VERSION_KEY);
    if (storedVersion !== CURRENT_DATA_VERSION) {
      await AsyncStorage.multiRemove([
        CERTS_STORAGE_KEY, ACTIVE_JOBS_STORAGE_KEY,
        '@commonplace_active_job', // old key cleanup
        EARNINGS_STORAGE_KEY, JOB_HISTORY_STORAGE_KEY,
        QUIZ_FAILURES_STORAGE_KEY, ACCOUNT_HOLD_STORAGE_KEY,
      ]);
      await AsyncStorage.setItem(DATA_VERSION_KEY, CURRENT_DATA_VERSION);
    }

    const [tokenRaw, certsRaw, jobsRaw, earningsRaw, historyRaw, driverRaw, failuresRaw, holdRaw] = await Promise.all([
      AsyncStorage.getItem(TOKEN_STORAGE_KEY),
      AsyncStorage.getItem(CERTS_STORAGE_KEY),
      AsyncStorage.getItem(ACTIVE_JOBS_STORAGE_KEY),
      AsyncStorage.getItem(EARNINGS_STORAGE_KEY),
      AsyncStorage.getItem(JOB_HISTORY_STORAGE_KEY),
      AsyncStorage.getItem(DRIVER_STORAGE_KEY),
      AsyncStorage.getItem(QUIZ_FAILURES_STORAGE_KEY),
      AsyncStorage.getItem(ACCOUNT_HOLD_STORAGE_KEY),
    ]);
    // Restore auth token — keeps user logged in across refreshes/closes. Token is the
    // single source of truth: only restore the driver profile when a token is also
    // present (a driver object with no token would render a half-authenticated UI).
    if (tokenRaw && driverRaw) {
      const driver = JSON.parse(driverRaw);
      if (driver && driver.name) {
        store.dispatch(authSlice.actions.setToken(tokenRaw));
        store.dispatch(setDriver(driver));
      }
    }
    if (certsRaw) {
      const certs = JSON.parse(certsRaw);
      if (Array.isArray(certs) && certs.length > 0) {
        store.dispatch(setCertifications(certs));
      }
    }
    if (jobsRaw) {
      const jobs = JSON.parse(jobsRaw);
      if (Array.isArray(jobs) && jobs.length > 0) {
        store.dispatch(jobsSlice.actions.setActiveJobs(jobs));
      }
    }
    if (earningsRaw) {
      const earnings = JSON.parse(earningsRaw);
      if (earnings && typeof earnings.totalEarnings === 'number') {
        store.dispatch(jobsSlice.actions.setEarnings(earnings));
      }
    }
    if (historyRaw) {
      const history = JSON.parse(historyRaw);
      if (Array.isArray(history) && history.length > 0) {
        store.dispatch(jobsSlice.actions.setCompletedJobs(history));
      }
    }
    if (failuresRaw) {
      const failures = JSON.parse(failuresRaw);
      if (failures && typeof failures === 'object') {
        store.dispatch(authSlice.actions.setQuizFailures(failures));
      }
    }
    if (holdRaw === 'true') {
      store.dispatch(authSlice.actions.setAccountOnHold(true));
    }

    // Refresh driver profile from backend on every app load (picks up fleet changes, etc.)
    if (tokenRaw) {
      try {
        const { api } = await import('../services/api');
        const profileRes = await api.get('/drivers/me');
        if (profileRes.data && profileRes.data.name) {
          store.dispatch(setDriver(profileRes.data));
        }
      } catch {
        // Backend unreachable — keep cached profile
      }
    }

    // Sync certifications bidirectionally if logged in
    if (tokenRaw) {
      try {
        const { api } = await import('../services/api');
        // First, push any local certs to backend (in case they weren't saved)
        const localCerts = store.getState().auth.certifications;
        if (localCerts.length > 0) {
          await api.post('/drivers/me/certifications/sync', { categorySlugs: localCerts }).catch(() => {});
        }
        // Then fetch from backend (source of truth). Local certs were already
        // pushed above, so the backend APPROVED set is authoritative here:
        // adopt it directly so admin-revoked certs are dropped locally.
        const { data } = await api.get('/drivers/me/certifications');
        if (Array.isArray(data)) {
          const backendCerts = data
            .filter((c: any) => c.status === 'APPROVED')
            .map((c: any) => c.categorySlug);
          store.dispatch(setCertifications(backendCerts));
          await AsyncStorage.setItem(CERTS_STORAGE_KEY, JSON.stringify(backendCerts));
        }
      } catch {
        // Backend unreachable — keep local certs as fallback (no change)
      }
    }
  } catch {
    // Ignore storage errors
  }
}

/** @deprecated Use loadPersistedState instead */
export const loadPersistedCertifications = loadPersistedState;
